import { Extension } from '@tiptap/react';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { DOMParser as PMDOMParser } from '@tiptap/pm/model';

import { v4 as uuid } from 'uuid';

import { fileTooLargeMessage, isOverUploadLimit } from 'utils/uploadLimits';
import { uploadAttachmentFunction, uploadBase64ImageToServer } from '../tiptap-node/attachment-node/utils';

export interface FileDropExtensionOptions {
  entityId: string;
  enqueueErrorSnackbar: (message: string) => void;
}

export const FileDropExtension = Extension.create<FileDropExtensionOptions>({
  name: 'fileDrop',

  addOptions() {
    return {
      entityId: '',
      enqueueErrorSnackbar: () => {},
    };
  },

  addProseMirrorPlugins() {
    const { editor, options } = this;

    return [
      new Plugin({
        key: new PluginKey('fileDrop'),
        props: {
          handleDrop: (view, event, _slice, moved) => {
            if (moved || !event.dataTransfer?.files?.length) {
              return false;
            }

            const files = Array.from(event.dataTransfer.files);
            if (files.length === 0) {
              return false;
            }

            const coords = { left: event.clientX, top: event.clientY };
            const pos = view.posAtCoords(coords);
            if (!pos) {
              return false;
            }

            event.preventDefault();

            uploadAttachmentFunction(editor, files, pos.pos, options.entityId, options.enqueueErrorSnackbar);

            return true;
          },
          handlePaste: (view, event) => {
            const files = Array.from(event.clipboardData?.files || []);
            if (files.length > 0) {
              const { from } = view.state.selection;
              event.preventDefault();
              uploadAttachmentFunction(editor, files, from, options.entityId, options.enqueueErrorSnackbar);
              return true;
            }

            // Replace base64 images in pasted HTML with blob URLs before inserting into
            // the document, so the large data never enters Yjs and stays within Tiptap
            // Cloud's size limit. Each image is then uploaded to the server.
            const html = event.clipboardData?.getData('text/html') || '';
            if (!html.includes('data:image/')) {
              return false;
            }

            const htmlDoc = new window.DOMParser().parseFromString(html, 'text/html');
            const imagesToUpload: Array<{ file: File; blobUrl: string; uploadId: string }> = [];

            htmlDoc.querySelectorAll('img').forEach((img) => {
              const src = img.getAttribute('src') || '';
              if (!src.startsWith('data:image/')) return;

              try {
                const [header, base64] = src.split(',');
                const mime = header.split(':')[1]?.split(';')[0] || 'image/png';
                const ext = mime.split('/')[1] || 'png';
                const byteString = atob(base64);
                const byteArray = new Uint8Array(byteString.length);
                for (let i = 0; i < byteString.length; i++) {
                  byteArray[i] = byteString.charCodeAt(i);
                }
                const file = new File([byteArray], `pasted-image.${ext}`, { type: mime });
                if (isOverUploadLimit(file)) {
                  options.enqueueErrorSnackbar(fileTooLargeMessage(file));
                  img.remove();
                  return;
                }
                const blobUrl = URL.createObjectURL(file);
                img.setAttribute('src', blobUrl);
                imagesToUpload.push({ file, blobUrl, uploadId: uuid() });
              } catch {
                img.remove();
              }
            });

            const pmParser = PMDOMParser.fromSchema(view.state.schema);
            const slice = pmParser.parseSlice(htmlDoc.body, {
              preserveWhitespace: 'full',
              context: view.state.selection.$from,
            });
            const tr = view.state.tr.replaceSelection(slice);
            tr.scrollIntoView();
            view.dispatch(tr);

            imagesToUpload.forEach(({ file, blobUrl, uploadId }) => {
              editor.state.doc.descendants((node, pos) => {
                if (node.type.name === 'image' && node.attrs.src === blobUrl) {
                  editor
                    .chain()
                    .setNodeSelection(pos)
                    .updateAttributes('image', { 'data-upload-id': uploadId, 'data-uploading': true })
                    .run();
                  void uploadBase64ImageToServer(
                    editor, file, blobUrl, uploadId, options.entityId, options.enqueueErrorSnackbar,
                  );
                  return false;
                }
                return true;
              });
            });

            return true;
          },
        },
      }),
    ];
  },
});
